import type { FC } from 'react';
import { Spinner } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetIcon } from '../../../../../../../components/icons';
import Plus from '../../../../../../../components/icons/icon-components/plus';
import { Text } from '../../../../../../../ui-components';
import { classNames } from '../../../../../../../utils/tailwind/classNames';
import { QuickAddToBagButtonVariant } from './QuickAddToBagButton.types';
import type { QuickAddToBagButtonProps } from './QuickAddToBagButton.types';

type QuickAddToBagButtonComponentType = FC<QuickAddToBagButtonProps> & {
    Variant: typeof QuickAddToBagButtonVariant;
};

const QuickAddToBagButton: QuickAddToBagButtonComponentType = ({
    onClick,
    isLoading,
    disabled,
    className,
    ariaLabel,
    dataTestId = 'search-page.product-tile.quick-add-button',
    variant,
}: QuickAddToBagButtonProps): JSX.Element | null => {
    const intl = useIntl();
    const isWhiteIconVariant = variant === QuickAddToBagButtonVariant.WhiteIcon;

    const recipeQuickAddAriaLabel =
        ariaLabel ??
        intl.formatMessage({
            id: 'recipe.recommendations.quick_add',
            defaultMessage: 'Quick add to bag',
        });

    const variantClassName = isWhiteIconVariant
        ? 'inline-flex h-10 w-10 items-center justify-center rounded-full bg-transparent'
        : 'flex cursor-pointer items-center justify-center gap-2 rounded-[32px] bg-slt-pepper-900 px-3 py-1';

    const isDisabled = disabled || isLoading;

    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={recipeQuickAddAriaLabel}
            disabled={isDisabled}
            data-testid={dataTestId}
            className={classNames(variantClassName, { 'opacity-50': isDisabled }, className)}
        >
            {((): JSX.Element | null => {
                switch (variant) {
                    case QuickAddToBagButtonVariant.WhiteIcon:
                        if (isLoading) {
                            return (
                                <span className="bg-beige-400 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slt-pepper-300">
                                    <Spinner size="sm" color="slt-pepper.500" />
                                </span>
                            );
                        }

                        return <GetIcon name="add-to-bag" size="md" />;
                    case QuickAddToBagButtonVariant.BlackText:
                        if (isLoading) {
                            return (
                                <Text size="small" color="white" style="bold">
                                    <FormattedMessage id="adding" defaultMessage="Adding..." />
                                </Text>
                            );
                        }

                        return (
                            <>
                                <Plus color="white" />
                                <Text size="small" color="white" style="bold">
                                    <FormattedMessage id="add" defaultMessage="Add" />
                                </Text>
                            </>
                        );
                    default:
                        return null;
                }
            })()}
        </button>
    );
};

QuickAddToBagButton.Variant = QuickAddToBagButtonVariant;

export { QuickAddToBagButton };
